import { KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { ServiceStatisticsGranularity } from '../../dto/ServiceStatisticsDTO';
import { useChartTokens } from './chartTokens';
import { PeakAnalysis } from './peaks';
import { addDays, escapeHtml, formatInt, formatMinutes, Measure, mondayOf, TimelinePoint } from './statisticsUtils';

interface TimelineChartProps {
    points: TimelinePoint[];
    /** Qué período es cada punto: define el formato de los rótulos y del tooltip. */
    granularity: ServiceStatisticsGranularity;
    measure: Measure;
    ariaLabel: string;
    /** Promedio ponderado del período para la línea de referencia (el mismo número del indicador); null: sin línea. */
    average: number | null;
    /** Picos y períodos con pocas atenciones, uno por posición de `points`; null con «Atenciones» (no hay picos). */
    analysis: PeakAnalysis | null;
    /** Click, tap o Enter sobre un período con atenciones: abre su detalle. `opener` es a donde vuelve el foco al cerrarlo. */
    onOpen: (point: TimelinePoint, opener: HTMLElement | null) => void;
}

const CHART_HEIGHT = 280;
const HALF_DAY_MS = 12 * 60 * 60 * 1000;
/** Marcador de un punto común, de un pico y del punto resaltado con el teclado. */
const MARKER_SIZE = 3;
const PEAK_MARKER_SIZE = 7;
const ACTIVE_MARKER_SIZE = 8;
/** ApexCharts avisa del click por dos eventos (`markerClick` y `dataPointSelection`): el segundo, dentro de este tiempo, se ignora. */
const DUPLICATE_CLICK_MS = 400;

/** Un punto del eje de tiempo: con `point` es un período dibujado; sin él, un extremo del rango que no tiene valor. */
interface PlotPoint {
    x: number;
    y: number | null;
    point: TimelinePoint | null;
    /** Posición del período en `points` (y en `analysis`). */
    index: number;
}

const SERIES_NAME_ID: Record<Measure, string> = {
    attentions: 'ServicesStatisticsSeriesAttentions',
    wait: 'ServicesStatisticsSeriesWait',
    service: 'ServicesStatisticsSeriesService',
};

const LIVE_MESSAGE_ID: Record<Measure, string> = {
    attentions: 'ServicesStatisticsPeaksLiveAttentions',
    wait: 'ServicesStatisticsPeaksLiveWait',
    service: 'ServicesStatisticsPeaksLiveService',
};

/** El período que está `count` períodos antes (negativo) o después de `start`. */
function shiftPeriod(start: Date, granularity: ServiceStatisticsGranularity, count: number): Date {
    if (granularity === 'DAY') return addDays(start, count);
    if (granularity === 'WEEK') return addDays(start, 7 * count);
    return new Date(start.getFullYear(), start.getMonth() + count, 1);
}

const capitalize = (text: string): string => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/** "06/07" (el orden de día y mes depende del idioma). */
const dayMonth = (intl: IntlShape, date: Date): string =>
    intl.formatDate(date, { day: '2-digit', month: '2-digit' });

/** Rótulo corto del eje: "06/07", "sem. 06/07" (el lunes de la semana) o "jul 26". */
export function axisLabel(intl: IntlShape, timestamp: number, granularity: ServiceStatisticsGranularity): string {
    const date = new Date(timestamp);
    if (granularity === 'DAY') return dayMonth(intl, date);
    if (granularity === 'WEEK') {
        return intl.formatMessage({ id: 'ServicesStatisticsWeekAxisLabel' }, { date: dayMonth(intl, mondayOf(date)) });
    }
    return intl.formatDate(new Date(date.getFullYear(), date.getMonth(), 1), { month: 'short', year: '2-digit' })
        .replace(/[.,]/g, '').replace(' de ', ' ');
}

/** Título completo del tooltip: "Lunes 06/07/2026", "Semana del 06/07 al 12/07" o "Julio 2026". */
export function periodTitle(intl: IntlShape, point: TimelinePoint, granularity: ServiceStatisticsGranularity): string {
    if (granularity === 'DAY') {
        return `${capitalize(intl.formatDate(point.start, { weekday: 'long' }))} `
            + intl.formatDate(point.start, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    if (granularity === 'WEEK') {
        return intl.formatMessage({ id: 'ServicesStatisticsWeekTitle' },
            { from: dayMonth(intl, point.start), to: dayMonth(intl, point.end) });
    }
    return capitalize(intl.formatDate(point.start, { month: 'long', year: 'numeric' }).replace(' de ', ' '));
}

/** Nombre del período en un chip: "Domingo 20/09" (sin año) o, con semanas y meses, el mismo título del tooltip. */
export function chipTitle(intl: IntlShape, point: TimelinePoint, granularity: ServiceStatisticsGranularity): string {
    if (granularity !== 'DAY') return periodTitle(intl, point, granularity);
    return `${capitalize(intl.formatDate(point.start, { weekday: 'long' }))} ${dayMonth(intl, point.start)}`;
}

/** Etiqueta corta de un pico en el gráfico: "Dom 20/09 · 162". */
export function peakLabel(intl: IntlShape, point: TimelinePoint, granularity: ServiceStatisticsGranularity, value: number): string {
    const name = granularity === 'DAY'
        ? `${capitalize(intl.formatDate(point.start, { weekday: 'short' }).replace('.', ''))} ${dayMonth(intl, point.start)}`
        : axisLabel(intl, point.start.getTime(), granularity);
    return `${name} · ${formatInt(value)}`;
}

/** El valor de la medida elegida en el período; null si no hay con qué calcularlo. */
function valueOf(point: TimelinePoint, measure: Measure): number | null {
    if (measure === 'attentions') return point.attentions;
    return measure === 'wait' ? point.avgWait : point.avgService;
}

interface PointFlags {
    peak: boolean;
    lowVolume: boolean;
}

/** El valor de la medida elegida y, si no es la cantidad, cuántas atenciones lo respaldan; después, las notas del período. */
function tooltipHtml(intl: IntlShape, point: TimelinePoint, measure: Measure, granularity: ServiceStatisticsGranularity,
                     flags: PointFlags): string {
    const name = intl.formatMessage({ id: SERIES_NAME_ID[measure] });
    const lines = [`<div class="ss-tip__title">${escapeHtml(periodTitle(intl, point, granularity))}</div>`];
    if (measure === 'attentions') {
        lines.push(`<div>${escapeHtml(name)}: ${formatInt(point.attentions)}</div>`);
    } else if (point.attentions > 0) {
        lines.push(`<div>${escapeHtml(name)}: ${formatMinutes(valueOf(point, measure))}</div>`);
        lines.push(`<div>${escapeHtml(intl.formatMessage({ id: 'ServicesStatisticsSeriesAttentions' }))}: ${formatInt(point.attentions)}</div>`);
    } else {
        lines.push(`<div>${escapeHtml(intl.formatMessage({ id: 'ServicesStatisticsNoAttentions' }))}</div>`);
    }
    if (flags.peak) {
        lines.push(`<div class="ss-tip__note ss-tip__note--peak">${escapeHtml(intl.formatMessage({ id: 'ServicesStatisticsPeaksTipPeak' }))}</div>`);
    }
    if (flags.lowVolume) {
        lines.push(`<div class="ss-tip__note">${escapeHtml(intl.formatMessage({ id: 'ServicesStatisticsPeaksTipLow' }))}</div>`);
    }
    if (point.attentions > 0) {
        lines.push(`<div class="ss-tip__note">${escapeHtml(intl.formatMessage({ id: 'ServicesStatisticsPeaksTipOpen' }))}</div>`);
    }
    return `<div class="ss-tip">${lines.join('')}</div>`;
}

/**
 * Evolución del período: barras de atenciones, o línea de espera o duración medias, con un punto por día, semana o mes.
 * El eje X es un eje de tiempo real (no categorías de texto): ApexCharts ralea solo los rótulos para que no se pisen y la
 * línea une los puntos aunque haya períodos sin atenciones. Esos períodos no se dibujan en la línea (no cuentan como cero
 * minutos); en las barras de atenciones sí valen cero.
 * Con espera o duración, una línea punteada marca el promedio del período, los períodos con pocas atenciones llevan un
 * marcador hueco (su promedio puede no ser representativo) y los picos, un marcador más grande con su etiqueta. Un click,
 * un tap o Enter sobre un período con atenciones abre su detalle; con el teclado, el contenedor es un grupo enfocable:
 * flechas e Inicio/Fin mueven un punto resaltado y una región `aria-live` dice dónde está (mismo patrón que
 * `ScheduleHoursChart`).
 * TODO(design-system): gráficos y mapa de calor. Novarium no define el estilo de los gráficos.
 * TODO(design-system): color de pico en gráficos. Se usa el par --warn / --warn-bg de la etiqueta «Atención» del ranking.
 */
export default function TimelineChart({ points, granularity, measure, ariaLabel, average, analysis, onOpen }: TimelineChartProps) {
    const intl = useIntl();
    const ref = useRef<HTMLDivElement>(null);
    const hintId = useId();
    const tokens = useChartTokens(ref);
    const isBar = measure === 'attentions';
    /** Posición en `plot` del período resaltado con el teclado (o al apuntarlo, no: eso lo hace el tooltip). */
    const [activePlot, setActivePlot] = useState<number | null>(null);
    const lastOpenRef = useRef(0);

    /**
     * Lo que se dibuja: todos los períodos en las barras, y solo los que tienen atenciones en las líneas. Se agregan
     * puntos sin valor en los extremos del rango, para que el eje de tiempo abarque todo el rango y ApexCharts no lo
     * achique a los puntos dibujados (con un solo punto no sabe dibujar los rótulos del eje). En las barras, y en un rango
     * de un solo período, van un período más allá de cada extremo: así la primera y la última barra no quedan cortadas ni
     * una sola ocupa todo el ancho.
     */
    const plot = useMemo<PlotPoint[]>(() => {
        if (points.length === 0) return [];
        const drawn: PlotPoint[] = points.flatMap((point, index) => {
            const y = valueOf(point, measure);
            return isBar || (point.attentions > 0 && y !== null) ? [{ x: point.start.getTime(), y, point, index }] : [];
        });
        const spare = isBar || points.length === 1 ? 1 : 0;
        const lowX = shiftPeriod(points[0].start, granularity, -spare).getTime();
        const highX = shiftPeriod(points[points.length - 1].start, granularity, spare).getTime();
        if (drawn.length === 0 || drawn[0].x > lowX) drawn.unshift({ x: lowX, y: null, point: null, index: -1 });
        if (drawn[drawn.length - 1].x < highX) drawn.push({ x: highX, y: null, point: null, index: -1 });
        return drawn;
    }, [points, isBar, measure, granularity]);

    const series = useMemo(() => [{
        name: intl.formatMessage({ id: SERIES_NAME_ID[measure] }),
        data: plot.map(({ x, y }) => ({ x, y })),
    }], [intl, plot, measure]);

    const flagsOf = useCallback((index: number): PointFlags => ({
        peak: analysis?.peaks.includes(index) ?? false,
        lowVolume: analysis?.lowVolume[index] ?? false,
    }), [analysis]);

    /** Los períodos a los que se llega con las flechas: los dibujados (en las barras, también los de cero atenciones). */
    const navigable = useMemo(() => plot.flatMap((item, position) => (item.point ? [position] : [])), [plot]);

    useEffect(() => { setActivePlot(null); }, [plot]);

    /** Abre el detalle del período en la posición `position` de `plot` (si tiene atenciones). */
    const openAt = useCallback((position: number) => {
        const point = plot[position]?.point;
        if (!point || point.attentions <= 0) return;
        const now = Date.now();
        if (now - lastOpenRef.current < DUPLICATE_CLICK_MS) return;
        lastOpenRef.current = now;
        setActivePlot(position);
        onOpen(point, ref.current);
    }, [plot, onOpen]);

    const options = useMemo<ApexOptions | null>(() => {
        if (!tokens || plot.length === 0 || !ref.current) return null;
        const rangeStart = points[0].start.getTime();
        const rangeEnd = points[points.length - 1].start.getTime();
        const warnBg = getComputedStyle(ref.current).getPropertyValue('--warn-bg').trim();
        const font = { fontFamily: tokens.font, fontSize: '12px' };

        // Marcadores huecos: períodos con atenciones, pero pocas (solo líneas)
        const hollow = isBar ? [] : plot.flatMap((item, position) => (item.point && analysis?.lowVolume[item.index]
            ? [{
                seriesIndex: 0, dataPointIndex: position, fillColor: tokens.card, strokeColor: tokens.primary,
                size: MARKER_SIZE, shape: 'circle' as const,
            }] : []));

        const peakAnnotations = plot.flatMap(item => {
            if (!item.point || item.y === null || !analysis?.peaks.includes(item.index)) return [];
            return [{
                x: item.x,
                y: item.y,
                marker: { size: PEAK_MARKER_SIZE, fillColor: tokens.warn, strokeColor: warnBg, strokeWidth: 2 },
                label: {
                    text: peakLabel(intl, item.point, granularity, item.y),
                    borderColor: tokens.warn,
                    borderWidth: 1,
                    offsetY: -4,
                    style: { ...font, color: tokens.warn, background: warnBg, fontWeight: 600 },
                },
            }];
        });

        const activeItem = activePlot === null ? null : plot[activePlot];
        const activeAnnotations = activeItem && activeItem.point && activeItem.y !== null ? [{
            x: activeItem.x,
            y: activeItem.y,
            marker: { size: ACTIVE_MARKER_SIZE, fillColor: tokens.card, strokeColor: tokens.textStrong, strokeWidth: 2 },
        }] : [];

        return {
            chart: {
                type: isBar ? 'bar' : 'line',
                height: CHART_HEIGHT,
                fontFamily: tokens.font,
                foreColor: tokens.text2,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
                events: {
                    // Con el tooltip compartido ApexCharts avisa del click con el período más cercano, no hace falta acertarle al punto
                    markerClick: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
                    dataPointSelection: (_event: unknown, _chart: unknown, config: { dataPointIndex: number }) => openAt(config.dataPointIndex),
                },
            },
            colors: [tokens.primary],
            states: { active: { filter: { type: 'none' } } },
            plotOptions: { bar: { borderRadius: 3, columnWidth: '70%' } },
            stroke: { width: isBar ? 0 : 2, curve: 'straight', lineCap: 'round' },
            markers: {
                size: isBar ? 0 : MARKER_SIZE,
                strokeWidth: isBar ? 0 : 2,
                strokeColors: tokens.primary,
                discrete: hollow,
            },
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: { borderColor: tokens.divider, strokeDashArray: 0 },
            annotations: {
                yaxis: !isBar && average !== null ? [{
                    y: average,
                    borderColor: tokens.text2,
                    strokeDashArray: 4,
                    label: {
                        text: intl.formatMessage({ id: 'ServicesStatisticsPeaksAverageLabel' }, { value: formatMinutes(average) }),
                        position: 'left',
                        textAnchor: 'start',
                        offsetX: 4,
                        borderWidth: 0,
                        style: { ...font, color: tokens.text2, background: 'transparent' },
                    },
                }] : [],
                points: [...peakAnnotations, ...activeAnnotations],
            },
            xaxis: {
                type: 'datetime',
                axisBorder: { color: tokens.divider },
                axisTicks: { color: tokens.divider },
                tooltip: { enabled: false },
                labels: {
                    rotate: 0,
                    datetimeUTC: false,
                    hideOverlappingLabels: true,
                    showDuplicates: false,
                    style: { fontSize: '12px' },
                    // Con un solo punto ApexCharts no pasa la marca de tiempo: el valor es la marca misma
                    formatter: (value: string, timestamp?: number) => {
                        const time = timestamp ?? Number(value);
                        // Los períodos vacíos de los extremos (ver `plot`) no llevan rótulo: no son parte del rango
                        if (!Number.isFinite(time) || time < rangeStart - HALF_DAY_MS || time > rangeEnd + HALF_DAY_MS) return '';
                        return axisLabel(intl, time, granularity);
                    },
                },
            },
            yaxis: {
                min: 0,
                forceNiceScale: true,
                title: {
                    text: intl.formatMessage({ id: isBar ? 'ServicesStatisticsAxisAttentions' : 'ServicesStatisticsAxisMinutes' }),
                    style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
                },
                labels: {
                    style: { fontSize: '12px' },
                    formatter: (value: number) => (isBar ? formatInt(value) : `${formatInt(value)} min`),
                },
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
                    // Un punto sin valor es un extremo del rango: se muestra el período dibujado más cercano
                    const item = plot[dataPointIndex]?.point
                        ? plot[dataPointIndex]
                        : (dataPointIndex <= 0 ? plot.find(candidate => candidate.point) : [...plot].reverse().find(candidate => candidate.point));
                    return item?.point ? tooltipHtml(intl, item.point, measure, granularity, flagsOf(item.index)) : '';
                },
            },
        };
    }, [intl, tokens, plot, points, isBar, measure, granularity, average, analysis, activePlot, openAt, flagsOf]);

    // ── Teclado ──

    /** Lo que se dice (y se ve) de un período: «Lunes 05/10/2026: espera media 50,7 min, 413 atenciones. Pico.» */
    const describe = (position: number): string => {
        const item = plot[position];
        if (!item?.point) return '';
        const period = periodTitle(intl, item.point, granularity);
        if (item.point.attentions <= 0) return intl.formatMessage({ id: 'ServicesStatisticsPeaksLiveEmpty' }, { period });
        const flags = flagsOf(item.index);
        const parts = [
            intl.formatMessage({ id: LIVE_MESSAGE_ID[measure] },
                { period, value: formatMinutes(valueOf(item.point, measure)), count: item.point.attentions }),
            flags.peak ? intl.formatMessage({ id: 'ServicesStatisticsPeaksLivePeak' }) : '',
            flags.lowVolume ? intl.formatMessage({ id: 'ServicesStatisticsPeaksLiveLow' }) : '',
        ];
        return parts.filter(Boolean).join(' ');
    };

    const move = (event: KeyboardEvent<HTMLDivElement>) => {
        if (navigable.length === 0) return;
        const current = activePlot === null ? -1 : navigable.indexOf(activePlot);
        let next: number | null = null;
        if (event.key === 'ArrowRight') next = Math.min(navigable.length - 1, current + 1);
        else if (event.key === 'ArrowLeft') next = current < 0 ? navigable.length - 1 : Math.max(0, current - 1);
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = navigable.length - 1;
        else if ((event.key === 'Enter' || event.key === ' ') && activePlot !== null) {
            event.preventDefault();
            openAt(activePlot);
            return;
        }
        if (next !== null) {
            event.preventDefault();
            setActivePlot(navigable[next]);
        }
    };

    const activeText = activePlot === null ? '' : describe(activePlot);

    return (
        <div className="ss-timeline">
            <div ref={ref} className="ss-chart ss-chart--interactive" role="group" aria-label={ariaLabel}
                aria-roledescription={intl.formatMessage({ id: 'ServicesStatisticsPeaksChartRole' })}
                aria-describedby={hintId} tabIndex={0}
                onKeyDown={move}
                onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActivePlot(null); }}>
                <div aria-hidden="true">
                    {options && (
                        <ReactApexChart key={`${measure}-${granularity}`} type={isBar ? 'bar' : 'line'} height={CHART_HEIGHT}
                            series={series} options={options} />
                    )}
                </div>
                <span id={hintId} className="nv-sr-only">{intl.formatMessage({ id: 'ServicesStatisticsPeaksChartHint' })}</span>
                <span className="nv-sr-only" role="status" aria-live="polite">{activeText}</span>
            </div>
            <p className="ss-timeline__detail nv-help" aria-hidden="true">
                {activeText}
            </p>
        </div>
    );
}
